import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye } from "react-icons/fa";

import { DetailedIndicatorC1Data } from "@/services/esus/indicator/esf/c1/getDetailedIndicatorC1";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";

import { TableSkeletons } from "@/components/TableSkeletons";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC1AttendanceTableColumns = {
	attendanceDate: Column;
	isScheduled: Column;
	attendanceTypeName: Column;
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	attendanceHealthTeam: Column;
};

type IndicatorEsfC1AttendancesTableProps = {
	attendances: DetailedIndicatorC1Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenId: number) => void;
};

export function IndicatorEsfC1AttendancesTable({
	attendances,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC1AttendancesTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC1AttendanceTableColumns>({
		attendanceDate: { title: "Data atendimento", visible: true },
		isScheduled: { title: "Programado", visible: true },
		attendanceTypeName: { title: "Tipo de atendimento", visible: true },
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		attendanceHealthTeam: { title: "Equipe/MC", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder">
					<th
						className={`text-center px-2 ${
							!columns.attendanceDate.visible && "d-none"
						}`}
					>
						{columns.attendanceDate.title}
					</th>
					<th className={`px-2 ${!columns.isScheduled.visible && "d-none"}`}>
						{columns.isScheduled.title}
					</th>
					<th className={`px-2 ${!columns.attendanceTypeName.visible && "d-none"}`}>
						{columns.attendanceTypeName.title}
					</th>
					<th className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.attendanceHealthTeam.visible && "d-none"}`}>
						{columns.attendanceHealthTeam.title}
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC1AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC1AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC1AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && attendances.length > 0
					? attendances.map((attendance, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td
									className={`text-center px-2 ${
										!columns.attendanceDate.visible && "d-none"
									}`}
								>
									{dayjs(attendance.attendanceDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.isScheduled.visible && "d-none"
									}`}
								>
									{attendance.isScheduled ? "SIM" : "NÃO"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.attendanceTypeName.visible && "d-none"
									}`}
								>
									{attendance.attendanceTypeName}
								</td>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{attendance.citizenName}</div>
										<div className="text-muted">
											{attendance.birthdate
												? dayjs(attendance.birthdate).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{attendance.birthdate ? getAge(attendance.birthdate) : ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{attendance.cpf
										? formatIdentifier(attendance.cpf)
										: attendance.cns ?? ""}
								</td>
								<td
									className={`px-2 ${
										!columns.attendanceHealthTeam.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{attendance.attendanceHealthTeamName ?? ""}</div>
										<div className="text-muted">{attendance.area ?? ""}</div>
									</div>
								</td>

								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(attendance.citizenId)}
										title="Histórico do cidadão"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
